/** venture/coterms.ts — 회사별 조건(FP·수수료·투자확인서 방식) 조회 헬퍼 (2026-09-24)
 *  등록정보(p.cos: Co)와 자료수집 카드(collect.cos: CoCard)는 사업자번호 또는 이름으로 짝을 맞춘다.
 *  회사별 값(FP·수수료·방식)은 등록정보 쪽(Co)에 산다. */
import { defaultFee, defaultCertPlan, FEE_EXTRAS } from './types';
type Extras = typeof FEE_EXTRAS;
import type { Project, Co, CoCard, FeeTerms, CertPlan, FeeBillTo } from './types';

const digits = (s: string | undefined | null): string => String(s || '').replace(/\D/g, '');

/** 자료수집 카드 → 등록정보 회사 (없으면 null) */
export function regCoOf(p: Project, card: CoCard | { name?: string; bizno?: string }): Co | null {
  const cos = p.cos || [];
  const bz = digits(card.bizno);
  if (bz) { const hit = cos.find((c) => digits(c.bizno) === bz); if (hit) return hit; }
  const nm = (card.name || '').trim();
  if (nm) { const hit = cos.find((c) => (c.name || '').trim() === nm); if (hit) return hit; }
  return null;
}

/** 등록정보 회사의 인덱스 (patch 용) */
export function regCoIndex(p: Project, card: CoCard | { name?: string; bizno?: string }): number {
  const co = regCoOf(p, card);
  return co ? (p.cos || []).indexOf(co) : -1;
}

/** 회사 1곳의 등록정보를 부분 수정하는 patch — 등록정보(reg)와 자료수집 카드 양쪽에서 쓴다 */
export function patchRegCo(p: Project, card: CoCard | { name?: string; bizno?: string }, patch: Partial<Co>): Partial<Project> | null {
  const i = regCoIndex(p, card);
  if (i < 0) return null;
  const cos = (p.cos || []).map((c, j) => (j === i ? { ...c, ...patch } : c));
  return { cos };
}

export const feeOf = (co: Co | null | undefined): FeeTerms => ({ ...defaultFee(), ...(co && co.fee ? co.fee : {}) });
export const certPlanOf = (co: Co | null | undefined): CertPlan => ({ ...defaultCertPlan(), ...(co && co.certPlan ? co.certPlan : {}) });

/** FP 표기 — "김도희 (한화생명금융서비스)" */
export function fpLabel(co: Co | null | undefined): string {
  if (!co || !co.fpName) return '';
  return co.fpName + (co.fpCo ? ' (' + co.fpCo + ')' : '');
}
/** 프로젝트의 FP 목록 — 회사별 FP를 중복 없이 (이름+소속 기준) */
export function fpList(p: Project): { name: string; co: string; tel: string; companies: string[] }[] {
  const out: { name: string; co: string; tel: string; companies: string[] }[] = [];
  (p.cos || []).forEach((c) => {
    if (!c.fpName) return;
    const key = c.fpName + '|' + (c.fpCo || '');
    const hit = out.find((x) => x.name + '|' + x.co === key);
    if (hit) { if (c.name) hit.companies.push(c.name); return; }
    out.push({ name: c.fpName, co: c.fpCo || '', tel: c.fpTel || '', companies: c.name ? [c.name] : [] });
  });
  return out;
}

/* ── 금액 ── */
/** 이 법인의 대표(GP 또는 LP)의 마스터 인적사항 — 13·14단계 대표자 생년월일·투자자 자동값 (2026-09-24) */
export function repPerson(p: Project, co: CoCard): { name: string; rrn: string; addr: string; tel: string } | null {
  const m = p.master; const c = p.collect;
  if (!m || !c) return null;
  if (co.rep === 'gp') return m.gp && m.gp.name ? m.gp : null;
  const lpId = (co.rep || '').startsWith('lp:') ? co.rep.slice(3) : '';
  const card = c.lps.find((x) => x.id === lpId);
  const lp = card ? (m.lps || []).find((x) => x.name === card.name) : undefined;
  return lp || null;
}
/** 주민번호 앞 6자리 + 뒷자리 첫 숫자 → 생년월일 (YYYY-MM-DD) · 못 읽으면 '' */
export function birthFromRrn(rrn: string): string {
  const d = (rrn || '').replace(/[^0-9]/g, '');
  if (d.length < 7) return '';
  const g = d.charAt(6); const cent = g === '1' || g === '2' || g === '5' || g === '6' ? '19' : g === '9' || g === '0' ? '18' : '20';
  return cent + d.slice(0, 2) + '-' + d.slice(2, 4) + '-' + d.slice(4, 6);
}

export const won = (s: string | number | undefined | null): number => {
  const n = parseInt(String(s == null ? '' : s).replace(/[^0-9]/g, ''), 10);
  return isNaN(n) ? 0 : n;
};
export const fmtWon = (n: number): string => n.toLocaleString('ko-KR') + '원';
/** 3,000,000 → "300만원" · 12,345,678 → "1,234.6만원" · 1억 이상 → "1.2억원" */
export function shortWon(n: number): string {
  if (!n) return '-';
  if (n >= 100000000) { const v = n / 100000000; return (Number.isInteger(v) ? v : Math.round(v * 10) / 10) + '억원'; }
  const m = n / 10000;
  return (Number.isInteger(m) ? m.toLocaleString('ko-KR') : (Math.round(m * 10) / 10).toLocaleString('ko-KR')) + '만원';
}

export interface FeeLine { key: string; label: string; amount: number; billTo: FeeBillTo; included: boolean; }
export interface FeeTotals {
  venture: number; vat: number; ventureTotal: number;   // 벤처 수수료 + 부가세
  lines: FeeLine[];                                      // 부대비용 (별도 청구분만 금액 합산)
  byBillTo: Record<FeeBillTo, number>;
  grand: number;
}
/** 청구 예정액 계산 — 포함(included) 항목은 벤처 수수료에 녹아 있으므로 합산하지 않는다 */
export function feeTotals(fee: FeeTerms, extras: Extras = FEE_EXTRAS): FeeTotals {   // extras = feeExtrasFor(회사) — LP 는 GP교육 빼고
  const venture = won(fee.amount);
  const vat = fee.vat === 'separate' ? Math.round(venture * 0.1) : 0;
  const ventureTotal = venture + vat;
  const lines: FeeLine[] = extras.map(([k, label]) => {
    const it = fee[k];
    return { key: k, label, amount: it.mode === 'separate' ? won(it.amount) : 0, billTo: it.billTo, included: it.mode === 'included' };
  });
  const byBillTo: Record<FeeBillTo, number> = { company: 0, fp: 0 };
  byBillTo[fee.billTo] += ventureTotal;
  lines.forEach((l) => { if (!l.included) byBillTo[l.billTo] += l.amount; });
  return { venture, vat, ventureTotal, lines, byBillTo, grand: byBillTo.company + byBillTo.fp };
}

/** 별도 청구 + 금액이 정해진 부대비용만 [키, 라벨] */
export const FEE_EXTRAS_SEP = (fee: FeeTerms, extras: Extras = FEE_EXTRAS): [string, string][] =>
  extras.filter(([k]) => fee[k].mode === 'separate' && won(fee[k].amount) > 0).map(([k, l]) => [k, l] as [string, string]);

export const TIMING_KO: Record<FeeTerms['timing'], string> = { post: '후불', pre: '선불', split: '계약금+잔금' };
export const BILLTO_KO: Record<FeeBillTo, string> = { company: '업체 청구', fp: 'FP 대납' };

/** 헤더·목록용 한 줄 요약 — "300만원 후불 · 부가세 별도 · FP 대납" */
export function feeSummary(fee: FeeTerms): string {
  const parts = [shortWon(won(fee.amount)) + ' ' + TIMING_KO[fee.timing], '부가세 ' + (fee.vat === 'separate' ? '별도' : '포함')];
  if (fee.billTo === 'fp') parts.push('FP 대납');
  return parts.join(' · ');
}

/** 법인별 투자금액 기본값 — 1곳이면 출자금 총액 전부, 2곳 이상이면 직접 적은 금액을 뺀 나머지를 똑같이 나눔(만원 단위, 남는 금액은 첫 곳)
 *  직접 적은 칸(amtAuto false 이고 값 있음)은 그대로 둔다 · 백엔드 tagmap.coAmts 와 같은 규칙 (2026-10-31 지도사) */
export function coAmtDefaults(total: number, cos: Array<Pick<Co, 'amt' | 'amtAuto'>>): Array<number | null> {
  const won = (v: string) => Number(String(v || '').replace(/[^0-9]/g, '')) || 0;
  if (cos.length === 1 && total > 0) return [total];   // 1곳이면 늘 전액(예전에 적어 둔 금액이 있어도) — 백엔드와 같음
  const manual = cos.map((c) => !c.amtAuto && won(c.amt) > 0);
  const autoIdx = cos.map((_, i) => i).filter((i) => !manual[i]);
  const rest = total - cos.reduce((a, c, i) => a + (manual[i] ? won(c.amt) : 0), 0);
  if (!autoIdx.length || total <= 0 || rest <= 0) return cos.map(() => null);
  const each = Math.floor(rest / autoIdx.length / 10000) * 10000;
  const out: Array<number | null> = cos.map(() => null);
  autoIdx.forEach((i, k) => { out[i] = k === 0 ? rest - each * (autoIdx.length - 1) : each; });
  return out;
}

/** 예시 이름 LP(임우승 · 빈칸 · LP1 …) — 안내장에서 OOO 로 보이는 LP · 그 출자금은 그 LP 가 들고 올 「아직 안 정한 법인」 몫 (백엔드 tagmap.lpShown 과 같은 규칙) */
export function isPlaceholderLp(name: string | undefined): boolean {
  const t = String(name || '').trim();
  return !t || t === '임우승' || /^LP\d+$/.test(t);
}
/** 예시 이름 LP 출자금 합계 — 등록 법인 투자금 기본값에서 뺀다 (2026-10-31 지도사) */
export function placeholderLpSum(lps: Array<{ name?: string; amt?: string }> | undefined): number {
  return (lps || []).filter((l) => isPlaceholderLp(l.name)).reduce((a, l) => a + (Number(String(l.amt || '').replace(/[^0-9]/g, '')) || 0), 0);
}
